import type { HttpTypes } from '@medusajs/types';
import { clx } from '@medusajs/ui';

import { getPercentageDiff } from '@lib/util/get-percentage-diff';
import { getPricesForVariant } from '@lib/util/get-product-price';
import { convertToLocale } from '@lib/util/money';
import Text from '@modules/common/components/text';

interface LineItemPriceProps {
  item: HttpTypes.StoreCartLineItem | HttpTypes.StoreOrderLineItem;
  style?: 'default' | 'tight';
  isCashOnly?: boolean;
}

const LineItemPrice = ({
  item,
  style = 'default',
  isCashOnly = false,
}: LineItemPriceProps) => {
  const prices = getPricesForVariant(item.variant);
  const currencyCode = prices?.currency_code ?? 'TWD';
  const calculatedPriceNumber = prices?.calculated_price_number ?? 0;
  const originalPriceNumber = prices?.original_price_number ?? 0;

  const adjustmentsSum = (item.adjustments || []).reduce(
    (acc, adjustment) => adjustment.amount + acc,
    0
  );

  const originalPrice = originalPriceNumber * item.quantity;
  const currentPrice = calculatedPriceNumber * item.quantity - adjustmentsSum;
  const hasReducedPrice = currentPrice < originalPrice;

  return (
    <div className="flex flex-col gap-x-2">
      <div>
        {hasReducedPrice && (
          <>
            <p>
              {style === 'default' && <span>Original: </span>}
              <span
                className="text-ui-fg-muted line-through"
                data-testid="product-original-price"
              >
                {convertToLocale({
                  amount: originalPrice,
                  currency_code: currencyCode,
                })}
              </span>
            </p>
            {style === 'default' && (
              <span className="text-ui-fg-interactive">
                -{getPercentageDiff(originalPrice, currentPrice || 0)}%
              </span>
            )}
          </>
        )}
        <Text
          variant="title-bold"
          as="span"
          className={clx('text-black-30', {
            'text-ui-fg-interactive': hasReducedPrice,
          })}
          data-testid="product-price"
        >
          {convertToLocale({
            amount: currentPrice,
            currency_code: currencyCode,
          })}
        </Text>
        {isCashOnly && (
          <span className="ml-2 inline-block whitespace-nowrap rounded border border-[#dcd7c5] bg-[#efebe0] px-2 py-[3px] text-xs font-medium tracking-wide text-[#9b7c53]">
            不適用回饋金
          </span>
        )}
      </div>
    </div>
  );
};

export default LineItemPrice;
